"use client";

import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { TicketSelector } from "@baluu/ui/components/checkout/ticket-selector";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import { BundleMembershipHelpPopup } from "@baluu/ui/components/checkout/bundle-membership-help-popup";
import type { TicketBookingPolicy } from "./utils/types";

type TicketType = {
  id: string;
  name: string;
  description?: string;
  price: number;
  maxQuantity: number;
  minQuantity?: number;
  soldOut?: boolean;
  // Session-specific ticket info
  numberOfGuestsPerTicket?: number;
  maxLimit?: number;
  currentSold?: number;
  isInTimeConstraint?: boolean;
  typeCharge?: "flat" | "perPerson" | "perPersonPerLesson" | "flatPerPerson";
  // Deposit ticket info
  ticketTypeGroup?: "regular" | "deposit";
  depositAmount?: number;
  // Computed prices (for perPersonPerLesson, multiplied by numberOfSessions)
  effectivePrice?: number;
  effectiveDepositAmount?: number;
  // Members-only ticket info
  membershipRequired?: boolean;
  membershipAccessGranted?: boolean;
  // Eligibility & policy
  eligibilityMessage?: string;
  policyHint?: string;
  bookingPolicy?: TicketBookingPolicy | null;
};

type TicketSelection = {
  ticketTypeId: string;
  quantity: number;
};

type TicketSelectionStepProps = {
  ticketTypes: TicketType[];
  selections: TicketSelection[];
  onSelectionChange: (ticketTypeId: string, quantity: number) => void;
  currency?: string;
  totalAvailability?: number;
  className?: string;
  hidePrice?: boolean;
  bundleMaxTickets?: number;
  isMultiDateEnabled?: boolean;
  dateCount?: number;
  membershipsUrl?: string;
  fallbackBookingPolicy?: import("./utils/types").TicketBookingPolicy | null;
  onPolicyClick?: (ticketName: string, policy: import("./utils/types").TicketBookingPolicy) => void;
  showBundleMembershipHelp?: boolean;
};

function TicketSelectionStep({
  ticketTypes,
  selections,
  onSelectionChange,
  currency = "GBP",
  totalAvailability,
  className,
  hidePrice = false,
  bundleMaxTickets,
  isMultiDateEnabled = false,
  dateCount = 1,
  membershipsUrl,
  fallbackBookingPolicy,
  onPolicyClick,
  showBundleMembershipHelp = false,
}: TicketSelectionStepProps) {
  const getQuantity = (ticketTypeId: string): number => {
    const selection = selections.find((s) => s.ticketTypeId === ticketTypeId);
    return selection?.quantity ?? 0;
  };

  const getTotalSelectedTickets = (): number => {
    return selections.reduce((sum, s) => sum + s.quantity, 0);
  };

  const getMaxQuantityForTicket = (ticketType: TicketType): number => {
    const currentQuantity = getQuantity(ticketType.id);
    const guestsPerTicket = ticketType.numberOfGuestsPerTicket ?? 1;

    // Calculate ticket-specific availability if maxLimit and currentSold are provided
    let ticketTypeAvailability = Infinity;
    if (
      ticketType.maxLimit !== undefined &&
      ticketType.currentSold !== undefined
    ) {
      // Available seats for this ticket type = maxLimit * guestsPerTicket - currentSold
      const availableSeats =
        ticketType.maxLimit * guestsPerTicket - ticketType.currentSold;
      // Convert seats to tickets
      ticketTypeAvailability = Math.floor(availableSeats / guestsPerTicket);
    }

    // Calculate session availability considering current selections
    let sessionAvailability = Infinity;
    if (totalAvailability !== undefined && totalAvailability > 0) {
      // For flat tickets, totalAvailability represents booking slots, not individual seats
      // So we don't divide by guestsPerTicket
      if (ticketType.typeCharge === "flat") {
        // Calculate how many flat tickets are currently selected
        const selectedFlatTickets = selections.reduce((sum, sel) => {
          const tt = ticketTypes.find((t) => t.id === sel.ticketTypeId);
          // Only count flat tickets in the selection
          if (tt?.typeCharge === "flat") {
            return sum + sel.quantity;
          }
          return sum;
        }, 0);
        // Add back our current selection
        sessionAvailability = totalAvailability - selectedFlatTickets + currentQuantity;
      } else {
        // For non-flat tickets, calculate based on seats
        // Calculate how many "seats" we currently have selected (in terms of guests)
        const selectedSeats = selections.reduce((sum, sel) => {
          const tt = ticketTypes.find((t) => t.id === sel.ticketTypeId);
          const ttGuestsPerTicket = tt?.numberOfGuestsPerTicket ?? 1;
          return sum + sel.quantity * ttGuestsPerTicket;
        }, 0);
        // Add back our current selection to get available seats
        const currentSeats = currentQuantity * guestsPerTicket;
        const availableSeats = totalAvailability - selectedSeats + currentSeats;
        // Convert to tickets
        sessionAvailability = Math.floor(availableSeats / guestsPerTicket);
      }
    }

    // Calculate bundle availability if bundleMaxTickets is set
    let bundleAvailability = Infinity;
    if (bundleMaxTickets !== undefined) {
      // Calculate how many tickets we currently have selected across ALL ticket types
      const totalSelectedTickets = getTotalSelectedTickets();
      // Add back our current selection to get available bundle tickets
      bundleAvailability = bundleMaxTickets - totalSelectedTickets + currentQuantity;
    }

    // Take the minimum of:
    // 1. Ticket type's explicit maxQuantity (from ticket type definition)
    // 2. Ticket type availability (based on maxLimit/currentSold)
    // 3. Session availability (overall spots left)
    // 4. Bundle availability (remaining bundle tickets)
    let maxTickets = Math.min(
      ticketType.maxQuantity,
      ticketTypeAvailability,
      sessionAvailability,
      bundleAvailability,
    );

    // If no limits set, use session availability or fallback to a reasonable max
    if (maxTickets === Infinity) {
      maxTickets = totalAvailability ?? ticketType.maxQuantity ?? 10;
    }

    // Flat tickets can only have 1
    if (ticketType.typeCharge === "flat") {
      return Math.min(1, maxTickets);
    }

    return Math.max(0, maxTickets);
  };

  const isMultiDate = isMultiDateEnabled && dateCount > 1;
  const [isHelpOpen, setHelpOpen] = React.useState(false);

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Tickets"
        description={
          isMultiDate
            ? "Select the number of people attending. The ticket will automatically apply to all dates."
            : "Select the number of tickets you'd like to purchase."
        }
      />

      {showBundleMembershipHelp && (
        <>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => setHelpOpen(true)}
          >
            <Info className="h-4 w-4" aria-hidden="true" />
            Have a bundle or membership? Tap to learn more
          </Button>

          <BundleMembershipHelpPopup
            open={isHelpOpen}
            onOpenChange={setHelpOpen}
          />
        </>
      )}

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Choose your tickets
        </p>

        <TicketSelector.Group>
          {ticketTypes.map((ticketType) => {
            const maxQuantity = getMaxQuantityForTicket(ticketType);
            // Sold out if: explicitly marked, out of time constraint, or no availability
            const isSoldOut =
              ticketType.soldOut ||
              ticketType.isInTimeConstraint === false ||
              maxQuantity === 0;

            // Use effectivePrice (includes perPersonPerLesson multiplier) if available
            const displayPrice = ticketType.effectivePrice ?? ticketType.price;
            const displayDepositAmount =
              ticketType.effectiveDepositAmount ?? ticketType.depositAmount;

            const effectiveBookingPolicy = ticketType.bookingPolicy ?? fallbackBookingPolicy ?? undefined;

            return (
              <TicketSelector
                key={ticketType.id}
                id={ticketType.id}
                name={ticketType.name}
                description={ticketType.description}
                price={hidePrice ? undefined : displayPrice}
                currency={currency}
                quantity={getQuantity(ticketType.id)}
                minQuantity={ticketType.minQuantity ?? 0}
                maxQuantity={maxQuantity}
                soldOut={isSoldOut}
                disabled={isSoldOut}
                onChange={(quantity) =>
                  onSelectionChange(ticketType.id, quantity)
                }
                ticketTypeGroup={ticketType.ticketTypeGroup}
                depositAmount={displayDepositAmount}
                fullPrice={displayPrice}
                priceSuffix={isMultiDate ? "/ per date" : undefined}
                membershipRequired={ticketType.membershipRequired}
                membershipAccessGranted={ticketType.membershipAccessGranted}
                membershipsUrl={membershipsUrl}
                eligibilityMessage={ticketType.eligibilityMessage}
                policyHint={ticketType.policyHint}
                bookingPolicy={effectiveBookingPolicy}
                onPolicyClick={onPolicyClick}
              />
            );
          })}
        </TicketSelector.Group>
      </div>
    </div>
  );
}

function formatTotal(
  ticketTypes: TicketType[],
  selections: TicketSelection[],
  currency: string,
): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const symbol = symbols[currency] || currency;

  const total = selections.reduce((sum, selection) => {
    const ticketType = ticketTypes.find((t) => t.id === selection.ticketTypeId);
    if (ticketType) {
      return sum + ticketType.price * selection.quantity;
    }
    return sum;
  }, 0);

  // Price is stored in minor units (cents/pence), divide by 100
  return `${symbol}${(total / 100).toFixed(2)}`;
}

export { TicketSelectionStep };
export type { TicketType, TicketSelection, TicketSelectionStepProps };
